import { MOCK_DATES, MOCK_NOTICE } from './constants';

export const bbaMock = {
  summary: {
    nucleus: 'Blockchain Business & Advertising',
    positioning: 'Strategic institutional agency nucleus for Axodus communication, growth, partnerships, and product positioning.',
    operatingMode: 'mock-read-only',
    activeServices: 9,
    activeCampaigns: 4,
    activePartners: 5,
    openProposals: 4,
    governanceAlerts: 3,
    deliverablesInProgress: 7,
    publicReputationRisk: 'medium',
    mockNotice: MOCK_NOTICE,
  },
  services: [
    {
      id: 'bba-service-brand-system',
      title: 'Institutional Brand System',
      slug: 'institutional-brand-system',
      category: 'Branding',
      subcategory: 'Identity Governance',
      shortDescription: 'Brand architecture, visual systems, and institutional communication standards.',
      description: 'Defines the visual and verbal identity framework for Axodus nuclei, partners, and DAO-facing products.',
      governanceStatus: 'review-ready',
      constitutionalStanding: 'compliant',
      pricingModel: 'proposal-based',
      deliveryModel: 'strategic-sprint',
      targetAudience: ['Internal Axodus Nucleus', 'DAO', 'Strategic Partner'],
      treasuryCompatible: true,
      active: true,
      maturity: 'mvp-ready',
      institutionalCategory: 'Identity',
      publicReputationRisk: 'low',
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-service-dao-growth',
      title: 'DAO Growth Architecture',
      slug: 'dao-growth-architecture',
      category: 'DAO Growth',
      subcategory: 'Community Acquisition',
      shortDescription: 'Governance-aware growth systems for DAO participation and member activation.',
      description: 'Maps acquisition channels, onboarding messages, and participation loops without speculative financial claims.',
      governanceStatus: 'under-review',
      constitutionalStanding: 'under-review',
      pricingModel: 'scope-based',
      deliveryModel: 'operational-program',
      targetAudience: ['DAO', 'Governance Partner'],
      treasuryCompatible: true,
      active: true,
      maturity: 'prototype',
      institutionalCategory: 'Growth',
      publicReputationRisk: 'medium',
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-service-product-launch',
      title: 'Web3 Product Launch Desk',
      slug: 'web3-product-launch-desk',
      category: 'Product Launch',
      subcategory: 'Launch Positioning',
      shortDescription: 'Launch planning for Axodus products, partner products, and marketplace services.',
      description: 'Coordinates positioning, channel readiness, deliverables, campaign narrative, and governance checkpoints.',
      governanceStatus: 'review-ready',
      constitutionalStanding: 'compliant',
      pricingModel: 'retainer-ready',
      deliveryModel: 'launch-room',
      targetAudience: ['Internal Axodus Nucleus', 'Company', 'Strategic Partner'],
      treasuryCompatible: false,
      active: true,
      maturity: 'mvp-ready',
      institutionalCategory: 'Launch',
      publicReputationRisk: 'medium',
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-service-governance-comms',
      title: 'Governance Communication Review',
      slug: 'governance-communication-review',
      category: 'Governance Communication',
      subcategory: 'Constitutional Messaging',
      shortDescription: 'Review layer for claims, public notices, proposal summaries, and treasury-sensitive messaging.',
      description: 'Validates that public communication remains aligned with constitutional standards and treasury-safe language.',
      governanceStatus: 'compliant',
      constitutionalStanding: 'compliant',
      pricingModel: 'internal-allocation',
      deliveryModel: 'review-workflow',
      targetAudience: ['Governance Partner', 'Internal Axodus Nucleus'],
      treasuryCompatible: true,
      active: true,
      maturity: 'mvp-ready',
      institutionalCategory: 'Governance',
      publicReputationRisk: 'low',
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-service-marketplace-packages',
      title: 'Marketplace Service Packaging',
      slug: 'marketplace-service-packaging',
      category: 'Strategic Partnerships',
      subcategory: 'Service Commercialization',
      shortDescription: 'Packages BBA services for future read-only Marketplace discovery.',
      description: 'Prepares service descriptions, delivery boundaries, governance review notes, and no-billing MVP listings.',
      governanceStatus: 'planning',
      constitutionalStanding: 'compliant',
      pricingModel: 'not-enabled',
      deliveryModel: 'catalog-preparation',
      targetAudience: ['Marketplace', 'Company', 'DAO'],
      treasuryCompatible: false,
      active: true,
      maturity: 'planned',
      institutionalCategory: 'Commerce',
      publicReputationRisk: 'low',
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
  ],
  campaigns: [
    {
      id: 'bba-campaign-axodus-institutional',
      title: 'Axodus Institutional Positioning',
      type: 'Institutional Communication',
      status: 'planning',
      clientId: 'bba-client-axodus-business',
      ecosystemLinked: true,
      governanceValidated: true,
      constitutionalStanding: 'compliant',
      objective: 'Clarify Axodus as a governance-first ecosystem with modular business nuclei.',
      channels: ['Website', 'Newsletter', 'Partner Briefings'],
      metrics: { reach: 12000, engagement: 7.4, qualifiedLeads: 18, sentiment: 'stable' },
      budgetModel: 'internal-allocation',
      treasuryExposure: 'none',
      publicReputationRisk: 'low',
      complianceRisks: ['Claims must remain institutional and non-speculative.'],
      startDate: MOCK_DATES.recent,
      endDate: MOCK_DATES.nextMonth,
      createdAt: MOCK_DATES.past,
    },
    {
      id: 'bba-campaign-dao-launch-kit',
      title: 'DAO Launch Kit Visibility',
      type: 'Product Launch',
      status: 'under-review',
      clientId: 'bba-client-marketplace',
      ecosystemLinked: true,
      governanceValidated: false,
      constitutionalStanding: 'under-review',
      objective: 'Prepare marketplace-facing positioning for DAO launch kits without enabling purchase flows.',
      channels: ['Marketplace', 'Website', 'Social Media'],
      metrics: { reach: 8400, engagement: 5.9, qualifiedLeads: 11, sentiment: 'watch' },
      budgetModel: 'mock-no-routing',
      treasuryExposure: 'review-required',
      publicReputationRisk: 'medium',
      complianceRisks: ['Avoid implying guaranteed DAO growth or token value.'],
      startDate: MOCK_DATES.recent,
      endDate: MOCK_DATES.nextMonth,
      createdAt: MOCK_DATES.past,
    },
    {
      id: 'bba-campaign-academy-comms',
      title: 'Academy Governance Communication Track',
      type: 'Educational Positioning',
      status: 'active',
      clientId: 'bba-client-academy',
      ecosystemLinked: true,
      governanceValidated: true,
      constitutionalStanding: 'compliant',
      objective: 'Position future Academy programs around DAO communication and Web3 institutional literacy.',
      channels: ['Academy', 'Newsletter', 'Community'],
      metrics: { reach: 5200, engagement: 8.1, qualifiedLeads: 24, sentiment: 'positive' },
      budgetModel: 'mock-internal',
      treasuryExposure: 'none',
      publicReputationRisk: 'low',
      complianceRisks: ['Certification claims remain preview-only until Academy approval.'],
      startDate: MOCK_DATES.recent,
      endDate: MOCK_DATES.nextMonth,
      createdAt: MOCK_DATES.past,
    },
    {
      id: 'bba-campaign-partner-intake',
      title: 'Strategic Partner Intake Narrative',
      type: 'Partnership Development',
      status: 'paused',
      clientId: 'bba-client-partner-dao',
      ecosystemLinked: false,
      governanceValidated: false,
      constitutionalStanding: 'restricted',
      objective: 'Prepare partner-facing communication while governance compatibility remains unresolved.',
      channels: ['Partner Briefings', 'Events'],
      metrics: { reach: 900, engagement: 3.2, qualifiedLeads: 2, sentiment: 'restricted' },
      budgetModel: 'not-enabled',
      treasuryExposure: 'blocked',
      publicReputationRisk: 'high',
      complianceRisks: ['Partner claims require verification before public publication.'],
      startDate: MOCK_DATES.recent,
      endDate: MOCK_DATES.nextMonth,
      createdAt: MOCK_DATES.past,
    },
  ],
  clientPartners: [
    {
      id: 'bba-client-axodus-business',
      name: 'Axodus Business',
      type: 'Internal Axodus Nucleus',
      governanceStanding: 'compliant',
      constitutionalBound: true,
      partnershipLevel: 'core',
      servicesActive: ['bba-service-brand-system', 'bba-service-product-launch'],
      treasuryCompatible: true,
      riskScore: 18,
      description: 'Business nucleus interface for institutional clients, product acceleration, and strategic consulting.',
    },
    {
      id: 'bba-client-marketplace',
      name: 'Axodus Marketplace',
      type: 'Internal Axodus Nucleus',
      governanceStanding: 'under-review',
      constitutionalBound: true,
      partnershipLevel: 'ecosystem',
      servicesActive: ['bba-service-marketplace-packages'],
      treasuryCompatible: false,
      riskScore: 34,
      description: 'Future discovery surface for BBA services and DAO launch packages. Billing is disabled in MVP.',
    },
    {
      id: 'bba-client-academy',
      name: 'Axodus Academy',
      type: 'Educational Partner',
      governanceStanding: 'compliant',
      constitutionalBound: true,
      partnershipLevel: 'ecosystem',
      servicesActive: ['bba-service-governance-comms'],
      treasuryCompatible: true,
      riskScore: 21,
      description: 'Education nucleus for future DAO communication programs and institutional literacy tracks.',
    },
    {
      id: 'bba-client-partner-dao',
      name: 'Partner Operator DAO',
      type: 'DAO',
      governanceStanding: 'restricted',
      constitutionalBound: true,
      partnershipLevel: 'candidate',
      servicesActive: ['bba-service-dao-growth'],
      treasuryCompatible: false,
      riskScore: 76,
      description: 'Candidate partner requiring governance verification before public partnership claims.',
    },
  ],
  proposals: [
    {
      id: 'bba-proposal-001',
      clientId: 'bba-client-axodus-business',
      title: 'Institutional Brand System Sprint',
      scope: 'Create the strategic identity map, message pillars, public communication rules, and governance-safe claim matrix.',
      status: 'review-ready',
      governanceReviewRequired: true,
      treasuryReviewRequired: false,
      operationalComplexity: 'medium',
      publicReputationRisk: 'low',
      deliveryEstimate: '3 weeks',
      createdAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-proposal-002',
      clientId: 'bba-client-marketplace',
      title: 'DAO Launch Kit Marketplace Preview',
      scope: 'Package BBA launch services as read-only Marketplace cards with no checkout, billing, or delivery automation.',
      status: 'under-review',
      governanceReviewRequired: true,
      treasuryReviewRequired: true,
      operationalComplexity: 'high',
      publicReputationRisk: 'medium',
      deliveryEstimate: '4 weeks',
      createdAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-proposal-003',
      clientId: 'bba-client-partner-dao',
      title: 'Partner Intake Communication Review',
      scope: 'Validate partner claims, operating model, and communication boundaries before any public announcement.',
      status: 'blocked',
      governanceReviewRequired: true,
      treasuryReviewRequired: false,
      operationalComplexity: 'medium',
      publicReputationRisk: 'high',
      deliveryEstimate: 'pending governance clearance',
      createdAt: MOCK_DATES.recent,
    },
  ],
  workflows: [
    {
      id: 'bba-workflow-intake',
      title: 'Client Intake Review',
      phase: 'intake',
      assignedAgents: ['BriefInterpreter', 'BrandStrategist'],
      governanceStatus: 'review-ready',
      operationalRisk: 'medium',
      blockers: [],
      validationRequired: true,
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-workflow-campaign-risk',
      title: 'Campaign Risk Review',
      phase: 'validation',
      assignedAgents: ['TrendAnalyst', 'DataAnalyst', 'AdsSpecialist'],
      governanceStatus: 'under-review',
      operationalRisk: 'high',
      blockers: ['Partner claim verification pending'],
      validationRequired: true,
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-workflow-institutional-approval',
      title: 'Institutional Approval',
      phase: 'approval',
      assignedAgents: ['CreativeDirector', 'AnalyticsAgent'],
      governanceStatus: 'compliant',
      operationalRisk: 'low',
      blockers: [],
      validationRequired: true,
      createdAt: MOCK_DATES.past,
      updatedAt: MOCK_DATES.recent,
    },
  ],
  brandAssets: [
    {
      id: 'bba-brand-001',
      title: 'Axodus Institutional Identity Kit',
      type: 'identity-system',
      format: 'guidelines',
      owner: 'Axodus Business',
      status: 'approved',
      governanceStatus: 'compliant',
      constitutionalStanding: 'compliant',
      publicReputationRisk: 'low',
      usageContext: ['Website', 'Partner Briefings', 'Proposal Document'],
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-brand-002',
      title: 'DAO Launch Kit Visual System',
      type: 'campaign-visuals',
      format: 'figma-ready',
      owner: 'Axodus Marketplace',
      status: 'under-review',
      governanceStatus: 'under-review',
      constitutionalStanding: 'under-review',
      publicReputationRisk: 'medium',
      usageContext: ['Marketplace', 'Social Media', 'Launch Report'],
      updatedAt: MOCK_DATES.recent,
    },
    {
      id: 'bba-brand-003',
      title: 'Governance Communication Templates',
      type: 'public-materials',
      format: 'template-pack',
      owner: 'Axodus Governance',
      status: 'approved',
      governanceStatus: 'compliant',
      constitutionalStanding: 'compliant',
      publicReputationRisk: 'low',
      usageContext: ['Newsletter', 'Proposal Document', 'Community'],
      updatedAt: MOCK_DATES.recent,
    },
  ],
  institutionalChannels: [
    {
      id: 'bba-channel-website',
      title: 'Axodus Website',
      type: 'website',
      status: 'active',
      owner: 'Axodus Business',
      governanceStatus: 'compliant',
      audience: 'Institutional visitors and ecosystem partners',
      publicReputationRisk: 'low',
      complianceNotes: ['No yield promises', 'Use governance-first positioning'],
    },
    {
      id: 'bba-channel-social',
      title: 'Social Media',
      type: 'social-media',
      status: 'review-gated',
      owner: 'BBA Agency',
      governanceStatus: 'under-review',
      audience: 'Community, partners, and prospective clients',
      publicReputationRisk: 'medium',
      complianceNotes: ['Claims require review', 'Avoid speculative token framing'],
    },
    {
      id: 'bba-channel-events',
      title: 'Institutional Events',
      type: 'events',
      status: 'planned',
      owner: 'BBA Agency',
      governanceStatus: 'review-ready',
      audience: 'DAO operators, companies, strategic partners',
      publicReputationRisk: 'medium',
      complianceNotes: ['Partnership claims must be verified before publication'],
    },
    {
      id: 'bba-channel-newsletter',
      title: 'Governance Newsletter',
      type: 'newsletter',
      status: 'active',
      owner: 'Axodus Governance',
      governanceStatus: 'compliant',
      audience: 'Governance participants and operators',
      publicReputationRisk: 'low',
      complianceNotes: ['Use transparent review language', 'Keep treasury notes read-only'],
    },
  ],
  deliverables: [
    {
      id: 'bba-deliverable-landing',
      title: 'Institutional Landing Page',
      type: 'landing-page',
      status: 'in-progress',
      campaignId: 'bba-campaign-axodus-institutional',
      clientId: 'bba-client-axodus-business',
      governanceReviewRequired: true,
      publicReputationRisk: 'low',
      dueDate: MOCK_DATES.nextMonth,
    },
    {
      id: 'bba-deliverable-pitch',
      title: 'Strategic Partner Pitch Deck',
      type: 'pitch-deck',
      status: 'review-ready',
      campaignId: 'bba-campaign-partner-intake',
      clientId: 'bba-client-partner-dao',
      governanceReviewRequired: true,
      publicReputationRisk: 'high',
      dueDate: MOCK_DATES.nextMonth,
    },
    {
      id: 'bba-deliverable-campaign-kit',
      title: 'DAO Launch Campaign Kit',
      type: 'campaign-kit',
      status: 'under-review',
      campaignId: 'bba-campaign-dao-launch-kit',
      clientId: 'bba-client-marketplace',
      governanceReviewRequired: true,
      publicReputationRisk: 'medium',
      dueDate: MOCK_DATES.nextMonth,
    },
    {
      id: 'bba-deliverable-report',
      title: 'Launch Readiness Report',
      type: 'launch-report',
      status: 'planned',
      campaignId: 'bba-campaign-academy-comms',
      clientId: 'bba-client-academy',
      governanceReviewRequired: false,
      publicReputationRisk: 'low',
      dueDate: MOCK_DATES.nextMonth,
    },
  ],
  portfolio: [
    {
      id: 'bba-portfolio-001',
      title: 'Axodus Institutional Architecture',
      category: 'Institutional Communication',
      clientId: 'bba-client-axodus-business',
      summary: 'Mock case study for ecosystem positioning, governance-safe messaging, and business nucleus alignment.',
      outcomes: ['Identity architecture', 'Communication standards', 'Partner briefing structure'],
      governanceStanding: 'compliant',
      publicReputationRisk: 'low',
    },
    {
      id: 'bba-portfolio-002',
      title: 'DAO Launch Kit Preview',
      category: 'Product Positioning',
      clientId: 'bba-client-marketplace',
      summary: 'Read-only product packaging concept for future Marketplace service discovery.',
      outcomes: ['Catalog narrative', 'Deliverable matrix', 'Governance review notes'],
      governanceStanding: 'under-review',
      publicReputationRisk: 'medium',
    },
    {
      id: 'bba-portfolio-003',
      title: 'Academy Communication Track',
      category: 'Education Positioning',
      clientId: 'bba-client-academy',
      summary: 'Future-facing education narrative around DAO communication, brand governance, and institutional literacy.',
      outcomes: ['Program positioning', 'Newsletter themes', 'Certification guardrails'],
      governanceStanding: 'compliant',
      publicReputationRisk: 'low',
    },
  ],
  governanceReviews: [
    {
      id: 'bba-review-001',
      title: 'Treasury-Safe Messaging',
      status: 'active',
      severity: 'high',
      description: 'Blocks unrealistic APY, yield, guaranteed returns, and financial outcome promises.',
      reasonCode: 'TREASURY_SAFE_MESSAGING_REQUIRED',
      affectedSurfaces: ['Campaigns', 'Deliverables', 'Institutional Channels'],
    },
    {
      id: 'bba-review-002',
      title: 'Public Reputation Risk',
      status: 'active',
      severity: 'medium',
      description: 'Flags exaggerated claims, unverified partnerships, and institutional misalignment before publication.',
      reasonCode: 'PUBLIC_REPUTATION_RISK_REVIEW',
      affectedSurfaces: ['Campaigns', 'Partner Briefings', 'Social Media'],
    },
    {
      id: 'bba-review-003',
      title: 'Constitutional Communication Compatibility',
      status: 'active',
      severity: 'constitutional',
      description: 'Ensures BBA communication stays compatible with Axodus governance standards.',
      reasonCode: 'CONSTITUTIONAL_COMMUNICATION_COMPATIBILITY',
      affectedSurfaces: ['Services', 'Proposals', 'Campaigns'],
    },
  ],
};
